'use client'

import React from 'react'
import Link from 'next/link'
import { track } from '@/utils/analytics'

/**
 * Compact inline nudge that points the reader at the Risk Checker quiz.
 *
 * Why this exists:
 * Our top-converting pages (per GSC) pull thousands of monthly impressions,
 * but `/risk-checker` only got 1 quiz start in the last 7 days — it has no
 * direct head-keyword pulling traffic of its own yet. Seeding it from the
 * top-5 traffic pages takes that page from "indexed but starved" to
 * "discovered by qualified mid-funnel readers."
 *
 * Design:
 *  - Mounts between BlogKeyTakeaways and the first H2 — the natural pause
 *    point where a reader decides "do I keep going or am I in the wrong
 *    place?" Pointing them at a 60-second personalised quiz at that
 *    moment converts at materially higher rates than the same link
 *    buried in body copy.
 *  - Visually distinct (left-border accent) without being heavy.
 *  - Tracked as `topic_hub_click` with a per-source label so GA4 Explore
 *    can break down quiz seeding by article.
 *  - `not-prose` so the parent `prose` typography doesn't restyle it.
 */
export interface RiskQuizInlineNudgeProps {
  /** Stable per-mount label — eg `blog_inline_kcups`, `blog_inline_stanley`.
   *  Lets GA4 attribute risk-checker traffic back to the source article. */
  source: string
  /** Optional copy override. Default fits any blog post; override for special
   *  cases (eg the cosmetics ingredient checker rather than risk checker). */
  text?: string
  /** Optional link text override (default: "Take the 60-second microplastic risk quiz"). */
  linkText?: string
  /** Optional destination override (default: /risk-checker). */
  href?: string
}

export default function RiskQuizInlineNudge({
  source,
  text = 'Not sure where you stand?',
  linkText = 'Take the 60-second microplastic risk quiz',
  href = '/risk-checker',
}: RiskQuizInlineNudgeProps) {
  return (
    <aside
      className="not-prose my-6 px-4 py-3 text-sm text-gray-700 bg-[#1E6B47]/8 border-l-2 border-[#1E6B47] rounded-r-lg leading-relaxed"
      aria-label="Take the microplastic risk quiz"
    >
      <span aria-hidden="true" className="text-[#1E6B47] font-semibold">→ </span>
      {text}{' '}
      <Link
        href={href}
        onClick={() =>
          track('topic_hub_click', {
            source,
            destination: href,
            page_type: 'blog',
          })
        }
        className="font-semibold text-[#1E6B47] underline hover:text-[#155235]"
      >
        {linkText}
      </Link>{' '}
      to see your top three swaps before you keep reading.
    </aside>
  )
}
